import type { DocField } from '../types'
import type { OpenApiSchemaNode, OpenApiSpec } from './openapiSchema.types'

const SCHEMA_REF_PREFIX = '#/components/schemas/'

/** Follows a `$ref` into `components.schemas`; returns the node unchanged if it isn't one. */
export function resolveRef(spec: OpenApiSpec, schema: OpenApiSchemaNode): OpenApiSchemaNode {
  if (!schema.$ref?.startsWith(SCHEMA_REF_PREFIX)) return schema
  const name = schema.$ref.slice(SCHEMA_REF_PREFIX.length)
  const target = spec.components?.schemas?.[name]
  return target ? { ...target, title: target.title ?? name } : schema
}

/** Collapses FastAPI's `anyOf: [X, {type: 'null'}]` optional-field pattern down to `X`. */
export function unwrapNullable(schema: OpenApiSchemaNode): OpenApiSchemaNode {
  if (!schema.anyOf) return schema
  const nonNull = schema.anyOf.filter(branch => branch.type !== 'null')
  return nonNull.length === 1 ? nonNull[0] : schema
}

function resolve(spec: OpenApiSpec, schema: OpenApiSchemaNode): OpenApiSchemaNode {
  return unwrapNullable(resolveRef(spec, unwrapNullable(schema)))
}

const TYPE_LABELS: Record<string, string> = {
  string: 'String',
  integer: 'Integer',
  number: 'Decimal',
  boolean: 'Boolean',
  object: 'Object'
}

/** Describes a schema using the same vocabulary `resources.ts` already uses. */
export function describeType(spec: OpenApiSpec, schema: OpenApiSchemaNode): string {
  const resolved = resolve(spec, schema)

  if (resolved.enum) return resolved.title ?? 'String'
  if (resolved.type === 'array') {
    const itemType = resolved.items ? describeType(spec, resolved.items) : 'String'
    return `Array<${itemType}>`
  }
  if (resolved.format === 'uuid') return 'UUID'
  if (resolved.format === 'date' || resolved.format === 'date-time') return 'Date'
  if (typeof resolved.type === 'string')
    return TYPE_LABELS[resolved.type] ?? resolved.title ?? 'String'
  return resolved.title ?? 'String'
}

const isNullable = (schema: OpenApiSchemaNode) =>
  schema.anyOf?.some(branch => branch.type === 'null') ?? false

// Response schemas are shallow; the limit only stops a self-referencing schema.
const MAX_FIELD_DEPTH = 5

/**
 * A description with the allowed values of an enum schema (or of an array of
 * enums) appended, unless the description already names every one of them in
 * backticks.
 */
export function withEnumValues(
  spec: OpenApiSpec,
  schema: OpenApiSchemaNode,
  description: string
): string {
  const resolved = resolve(spec, schema)
  const values =
    resolved.enum ??
    (resolved.type === 'array' && resolved.items ? resolve(spec, resolved.items).enum : undefined)
  const names = (values ?? []).map(String)
  if (names.length === 0 || names.every(name => description.includes(`\`${name}\``))) {
    return description
  }
  const list = names.map(name => `\`${name}\``).join(', ')
  return description ? `${description} One of ${list}.` : `One of ${list}.`
}

/**
 * Turns an object schema's `properties` into the `DocField[]` shape the doc
 * tables render, including nested objects and arrays of objects as dotted
 * paths (`vitals[].data[].values`), so every field of a response is listed.
 */
export function flattenObjectFields(
  spec: OpenApiSpec,
  schema: OpenApiSchemaNode,
  prefix = '',
  depth = 0
): DocField[] {
  const resolved = resolve(spec, schema)
  if (!resolved.properties || depth >= MAX_FIELD_DEPTH) return []
  const required = new Set(resolved.required ?? [])

  return Object.entries(resolved.properties).flatMap(([name, propSchema]) => {
    const resolvedProp = resolve(spec, propSchema)
    const path = `${prefix}${name}`
    const field: DocField = {
      name: path,
      type: describeType(spec, propSchema),
      required: required.has(name),
      nullable: isNullable(propSchema),
      description: withEnumValues(
        spec,
        propSchema,
        propSchema.description ?? resolvedProp.description ?? resolvedProp.title ?? ''
      )
    }
    const item =
      resolvedProp.type === 'array' && resolvedProp.items ? resolve(spec, resolvedProp.items) : null
    const children = item
      ? flattenObjectFields(spec, item, `${path}[].`, depth + 1)
      : flattenObjectFields(spec, resolvedProp, `${path}.`, depth + 1)
    return [field, ...children]
  })
}

/**
 * Flattens a JSON response schema, unwrapping this backend's success/collection
 * envelope (`{ success, data, metadata? }`) down to the fields of `data` — a single
 * object, or the item type when `data` is an array. The envelope itself is
 * documented once, in the overview's Standard Responses section.
 */
export function flattenResponseFields(spec: OpenApiSpec, schema: OpenApiSchemaNode): DocField[] {
  const resolved = resolve(spec, schema)
  const dataSchema = resolved.properties?.data
  if (!dataSchema) return flattenObjectFields(spec, resolved)

  const resolvedData = resolve(spec, dataSchema)
  const itemSchema = resolvedData.type === 'array' ? resolvedData.items : resolvedData
  return itemSchema ? flattenObjectFields(spec, itemSchema) : []
}
